import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatrixAxis, isMatrixAxisCalculationKey } from '../../_models/table';
import { QuestionsService } from '_services/questions.service';
import { CalcConfigService } from '_services/calc-config.service';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { KeySelectComponent } from '../key-select/key-select.component';

@Component({
    selector: 'app-matrix-axis-input',
    templateUrl: './matrix-axis-input.component.html',
    styleUrls: ['./matrix-axis-input.component.css'],
    imports: [
        AsyncPipe,
        FormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        KeySelectComponent,
    ],
})
export class MatrixAxisInputComponent implements OnChanges {
    @Input({ required: true }) title = '';
    @Input({ required: true }) axis!: MatrixAxis;
    /** When true, only band editors (breakpoints / lookup from question) are shown; input type and source are read-only in the parent. */
    @Input() hideInputSelectors = false;
    @Output() axisStructureChange = new EventEmitter<void>();

    inputType: 'Question' | 'Calculation' | 'Formula' = 'Question';

    /** Maximum number of numeric breakpoints (bands) per axis. */
    readonly maxBreakpointBands = 30;

    constructor(
        public qs: QuestionsService,
        public cc: CalcConfigService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.axis) {
            return;
        }
        if (changes['axis']) {
            this.syncFromAxis();
            if (!changes['axis'].firstChange) {
                this.axisStructureChange.emit();
            }
            return;
        }
        if (changes['hideInputSelectors']) {
            this.syncFromAxis();
        }
    }

    syncFromAxis(): void {
        if (this.axis.formulaId) {
            this.inputType = 'Formula';
        } else if (isMatrixAxisCalculationKey(this.axis.keyId)) {
            this.inputType = 'Calculation';
        } else {
            this.inputType = 'Question';
        }
        this.coerceAxisBandsToMatchInput(false);
    }

    /** True once this axis has a question/calculation key or a formula (bands are shown only then). */
    get hasAxisInputSource(): boolean {
        return !!(this.axis?.keyId || this.axis?.formulaId);
    }

    /** Code questions use lookup bands from choices; everything else uses numeric breakpoints. */
    get isLookupAxis(): boolean {
        if (this.inputType !== 'Question' || !this.axis.keyId) {
            return false;
        }
        return this.qs.getQuestion(this.axis.keyId)?.format === 'Code';
    }

    get lookupChoiceRows(): { label: string; value: string }[] {
        const q = this.axis.keyId ? this.qs.getQuestion(this.axis.keyId) : undefined;
        if (!q?.choices?.length) {
            return [];
        }
        return q.choices.map((c) => ({ label: c.label, value: c.value }));
    }

    /** Saved lookup bands when present; otherwise live question choices. */
    get lookupBandRows(): { label: string; value: string }[] {
        if (this.axis.lookupValues?.length) {
            return this.axis.lookupValues.map((value) => {
                const match = this.lookupChoiceRows.find((row) => row.value === value);
                return { label: match?.label ?? value, value };
            });
        }
        return this.lookupChoiceRows;
    }

    onInputTypeChange(): void {
        if (this.inputType === 'Formula') {
            this.axis.keyId = undefined;
        } else {
            this.axis.formulaId = undefined;
            this.axis.formula = undefined;
            if (this.inputType === 'Calculation' && !isMatrixAxisCalculationKey(this.axis.keyId)) {
                this.axis.keyId = undefined;
            }
            if (this.inputType === 'Question' && isMatrixAxisCalculationKey(this.axis.keyId)) {
                this.axis.keyId = undefined;
            }
        }
        this.coerceAxisBandsToMatchInput(true);
        this.emitStructureChange();
    }

    onQuestionSelected(questionId: string | null): void {
        this.axis.keyId = questionId ?? undefined;
        this.axis.formulaId = undefined;
        this.axis.formula = undefined;
        this.inputType = 'Question';
        this.coerceAxisBandsToMatchInput(true);
        this.emitStructureChange();
    }

    onFormulaSelected(formulaId: string | null): void {
        if (!formulaId) {
            this.axis.formulaId = undefined;
            this.axis.formula = undefined;
            this.coerceAxisBandsToMatchInput(true);
            this.emitStructureChange();
            return;
        }
        this.axis.formulaId = formulaId;
        this.axis.formula = this.cc.getFormula(formulaId) ?? undefined;
        this.axis.keyId = undefined;
        this.inputType = 'Formula';
        this.coerceAxisBandsToMatchInput(true);
        this.emitStructureChange();
    }

    setKeyId(event: unknown): void {
        if (typeof event === 'string') {
            this.axis.keyId = event;
            this.axis.formulaId = undefined;
            this.axis.formula = undefined;
            this.inputType = 'Calculation';
            this.coerceAxisBandsToMatchInput(true);
            this.emitStructureChange();
        }
    }

    /**
     * Align breakpoints vs lookup with the current input type and key, mirroring 1D table behavior.
     * Code lookup bands are only rebuilt when `refreshCodeLookup` is true (explicit input change).
     */
    private coerceAxisBandsToMatchInput(refreshCodeLookup: boolean): void {
        if (this.inputType === 'Formula') {
            this.axis.lookupValues = undefined;
            if (!this.axis.breakpoints?.length) {
                this.axis.breakpoints = [0];
            }
            return;
        }
        if (this.inputType === 'Calculation') {
            this.axis.lookupValues = undefined;
            if (!this.axis.breakpoints?.length) {
                this.axis.breakpoints = [0];
            }
            return;
        }
        // Question
        if (this.axis.keyId) {
            const q = this.qs.getQuestion(this.axis.keyId);
            if (q?.format === 'Code') {
                this.axis.breakpoints = undefined;
                if (refreshCodeLookup || !this.axis.lookupValues?.length) {
                    this.axis.lookupValues = q.choices?.map((c) => c.value) ?? [];
                }
                return;
            }
            if (q) {
                this.axis.lookupValues = undefined;
                if (!this.axis.breakpoints?.length) {
                    this.axis.breakpoints = [0];
                }
                return;
            }
        }
        this.axis.lookupValues = undefined;
        if (!this.axis.breakpoints?.length) {
            this.axis.breakpoints = [0];
        }
    }

    onBreakpointCountInput(ev: Event): void {
        const raw = (ev.target as HTMLInputElement).value;
        const n = raw === '' ? 1 : Number(raw);
        this.setBreakpointBandCount(n);
    }

    setBreakpointBandCount(raw: number | string): void {
        let n = typeof raw === 'string' ? parseInt(raw, 10) : Math.floor(Number(raw));
        if (!Number.isFinite(n) || n < 1) {
            n = 1;
        }
        n = Math.min(n, this.maxBreakpointBands);
        const cur = [...(this.axis.breakpoints ?? [0])];
        while (cur.length < n) {
            const last = cur[cur.length - 1] ?? 0;
            cur.push(typeof last === 'number' && Number.isFinite(last) ? last + 1 : 0);
        }
        while (cur.length > n) {
            cur.pop();
        }
        this.axis.breakpoints = cur;
        this.emitStructureChange();
    }

    emitStructureChange(): void {
        this.axisStructureChange.emit();
    }
}
